/**
 * Ancrage d'un menu contextuel SUR LE CURSEUR plutôt que sur son déclencheur.
 *
 * POURQUOI. Fluent ancre un `MenuPopover` sur son `MenuTrigger` — dans les
 * listes, le bouton ⋯ en bout de ligne. Un clic droit n'importe où sur la ligne
 * ouvrait donc le menu collé à l'extrémité DROITE : le geste et son résultat
 * n'étaient pas au même endroit, et sur une ligne large le menu apparaissait à
 * plusieurs centaines de pixels du curseur.
 *
 * `positioning.target` accepte un « élément virtuel » : un objet qui sait
 * seulement décrire son rectangle. C'est le point d'entrée prévu par Fluent pour
 * exactement ce cas (son helper interne `createVirtualElementFromClick` porte le
 * commentaire « can be used as a target for popper in scenarios such as context
 * menus »). Un rectangle de taille nulle placé sur le curseur suffit.
 *
 * TYPE LOCAL, ET NON `PositioningVirtualElement` DE FLUENT. Ce type — comme le
 * helper ci-dessus — n'est exporté que par `@fluentui/react-positioning`, un
 * paquet interne qui n'est PAS une dépendance déclarée de l'add-in. L'importer
 * ne marcherait que par accident de hissage (dépendance fantôme) et casserait
 * au premier changement d'arbre pnpm. La forme attendue est structurelle, donc
 * un type local est accepté à l'identique.
 *
 * Ce module ne fait que factoriser ce que la page détail faisait déjà chez elle ;
 * l'arbre de la page principale, lui, ancrait encore ses menus sur le bouton ⋯.
 */
export type CursorTarget = { getBoundingClientRect: () => DOMRect };

/** Le point (x, y) — coordonnées client — exposé comme ancre de positionnement. */
export function pointTarget(x: number, y: number): CursorTarget {
  const rect = {
    x,
    y,
    top: y,
    left: x,
    right: x,
    bottom: y,
    width: 0,
    height: 0,
    toJSON: () => '',
  } as DOMRect;
  // Rectangle figé et partagé : `getBoundingClientRect` est appelé plusieurs fois
  // par Fluent pendant le placement, et doit répondre la même chose à chaque fois.
  return { getBoundingClientRect: () => rect };
}

/**
 * Le `positioning` d'un menu de ligne : sur le curseur quand il a été ouvert au
 * clic droit, sinon sous le bouton ⋯ comme avant.
 *
 * `below`/`start` et non `below-end` pour le curseur : le menu doit se déplier
 * vers la droite DEPUIS le pointeur. Fluent le rabat tout seul près d'un bord.
 */
export function menuPositioning(target: CursorTarget | null) {
  return target ? { target, position: 'below' as const, align: 'start' as const } : ('below-end' as const);
}
